import { Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum } from "@/lib/format";
import { AlertCircle, CheckCircle2, Hash, Search, Tag, FileCode, ArrowDownUp, Layers } from "lucide-react";

interface NcmInfo { ncm: string; produtos: Set<string>; vTotal: number; count: number; }
interface CfopInfo { cfop: string; tipo: string; descricao: string; vTotal: number; count: number; notas: number; }

function cfopTipo(cfop: string): string {
  const ini = cfop[0];
  if (["1", "2", "3"].includes(ini)) return "Entrada";
  if (["5", "6", "7"].includes(ini)) return "Saída";
  return "Outro";
}

// Descrições simplificadas de CFOPs comuns
const CFOP_DESC: Record<string, string> = {
  "1102": "Compra para comercialização", "1202": "Devolução de venda",
  "1556": "Compra de material para uso/consumo", "2102": "Compra para comercialização (outra UF)",
  "5102": "Venda de mercadoria adquirida", "5202": "Devolução de compra",
  "5405": "Venda mercadoria com ST", "5949": "Outra saída não especificada",
  "6102": "Venda para outra UF", "6949": "Outra saída (outra UF)",
};

type SortKeyNCM = "ncm" | "count" | "vTotal";
type SortKeyCFOP = "cfop" | "tipo" | "notas" | "count" | "vTotal";

export default function ClassificacaoFiscalPage() {
  const { notas, dashboard } = useFiscalStore();
  const [tab, setTab] = useState<"ncm" | "cfop">("ncm");
  const [q, setQ] = useState("");
  
  const [sortKeyNcm, setSortKeyNcm] = useState<SortKeyNCM>("vTotal");
  const [sortDirNcm, setSortDirNcm] = useState<"asc" | "desc">("desc");
  
  const [sortKeyCfop, setSortKeyCfop] = useState<SortKeyCFOP>("vTotal");
  const [sortDirCfop, setSortDirCfop] = useState<"asc" | "desc">("desc");

  useEffect(() => { document.title = "Classificação Fiscal — Extrator Fiscal"; }, []);

  const { ncmMap, cfopMap, ncmSemDados, cfopInconsistentes, totalGeral } = useMemo(() => {
    const ncmMap = new Map<string, NcmInfo>();
    const cfopMap = new Map<string, CfopInfo>();
    const cfopPorNota = new Map<string, Set<string>>();
    let ncmSemDados = 0;
    let totalGeral = 0;
    const cfopInconsistentes: string[] = [];

    for (const nota of notas) {
      const notaCfops = cfopPorNota.get(nota.chave) ?? new Set<string>();
      for (const item of nota.itens ?? []) {
        totalGeral += item.vProd;
        
        // NCM
        if (!item.ncm || item.ncm.length < 8) ncmSemDados++;
        const ncmKey = item.ncm || "SEM NCM";
        const ncmEntry = ncmMap.get(ncmKey) ?? { ncm: ncmKey, produtos: new Set(), vTotal: 0, count: 0 };
        ncmEntry.produtos.add(item.xProd);
        ncmEntry.vTotal += item.vProd;
        ncmEntry.count += 1;
        ncmMap.set(ncmKey, ncmEntry);
        // CFOP
        const cfop = item.cfop || "SEM CFOP";
        const cfopEntry = cfopMap.get(cfop) ?? { cfop, tipo: cfopTipo(cfop), descricao: CFOP_DESC[cfop] ?? "—", vTotal: 0, count: 0, notas: 0 };
        cfopEntry.vTotal += item.vProd;
        cfopEntry.count += 1;
        cfopMap.set(cfop, cfopEntry);
        notaCfops.add(cfop);
      }
      cfopPorNota.set(nota.chave, notaCfops);
    }

    // Contar notas por CFOP
    for (const [, cfops] of cfopPorNota) {
      for (const cfop of cfops) {
        const entry = cfopMap.get(cfop);
        if (entry) entry.notas++;
      }
    }

    // Detectar CFOPs inconsistentes
    for (const nota of notas) {
      const tpNF = (nota.detalhes?.ide as any)?.tpNF;
      for (const item of nota.itens ?? []) {
        const ini = item.cfop?.[0] ?? "";
        if (tpNF === "0" && ["5","6","7"].includes(ini)) cfopInconsistentes.push(nota.chave);
        if (tpNF === "1" && ["1","2","3"].includes(ini)) cfopInconsistentes.push(nota.chave);
      }
    }

    return { ncmMap, cfopMap, ncmSemDados, cfopInconsistentes: [...new Set(cfopInconsistentes)], totalGeral };
  }, [notas]);

  const ncmList = useMemo(() => {
    const ql = q.toLowerCase();
    return [...ncmMap.values()]
      .filter((n) => !ql || n.ncm.includes(ql) || [...n.produtos].some((p) => p.toLowerCase().includes(ql)))
      .sort((a, b) => {
        const factor = sortDirNcm === "asc" ? 1 : -1;
        if (sortKeyNcm === "ncm") return a.ncm.localeCompare(b.ncm) * factor;
        return (a[sortKeyNcm] - b[sortKeyNcm]) * factor;
      });
  }, [ncmMap, q, sortKeyNcm, sortDirNcm]);

  const cfopList = useMemo(() => {
    const ql = q.toLowerCase();
    return [...cfopMap.values()]
      .filter((c) => !ql || c.cfop.includes(ql) || c.descricao.toLowerCase().includes(ql))
      .sort((a, b) => {
        const factor = sortDirCfop === "asc" ? 1 : -1;
        if (sortKeyCfop === "cfop") return a.cfop.localeCompare(b.cfop) * factor;
        if (sortKeyCfop === "tipo") return a.tipo.localeCompare(b.tipo) * factor;
        return (a[sortKeyCfop as 'notas'|'count'|'vTotal'] - b[sortKeyCfop as 'notas'|'count'|'vTotal']) * factor;
      });
  }, [cfopMap, q, sortKeyCfop, sortDirCfop]);

  const toggleSortNcm = (key: SortKeyNCM) => {
    if (sortKeyNcm === key) setSortDirNcm(d => d === "asc" ? "desc" : "asc");
    else { setSortKeyNcm(key); setSortDirNcm("desc"); }
  };

  const toggleSortCfop = (key: SortKeyCFOP) => {
    if (sortKeyCfop === key) setSortDirCfop(d => d === "asc" ? "desc" : "asc");
    else { setSortKeyCfop(key); setSortDirCfop("desc"); }
  };

  const tabStyle = (active: boolean) =>
    `flex-1 py-3 px-6 rounded-xl text-sm font-bold transition-all duration-300 flex items-center justify-center gap-2 ${active ? "bg-primary text-white shadow-glow" : "bg-transparent text-muted-foreground hover:bg-white/10 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white glass"}`;

  if (!dashboard) return <Navigate to="/upload" replace />;

  return (
    <div className="px-8 py-8 max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight dark:text-white">Classificação Fiscal</h1>
          <p className="text-sm text-muted-foreground mt-1">Inteligência de NCMs e CFOPs processados, com validação de consistência e volumetria.</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl">
          <Layers className="size-4 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-medium text-blue-900 dark:text-blue-200">
            {fmtNum(ncmMap.size + cfopMap.size)} Classificações Extraídas
          </span>
        </div>
      </header>

      {/* KPIs com Estilo Glass */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="NCMs Únicos" value={fmtNum(ncmMap.size)} sub="Nomenclaturas identificadas" icon={Hash} color="text-blue-500" />
        <KpiCard label="CFOPs Únicos" value={fmtNum(cfopMap.size)} sub="Operações mapeadas" icon={Tag} color="text-purple-500" />
        <KpiCard label="Itens s/ NCM Válido" value={fmtNum(ncmSemDados)} sub="Erros de cadastro" icon={AlertCircle} color={ncmSemDados > 0 ? "text-red-500" : "text-emerald-500"} />
        <KpiCard label="CFOPs Inconsistentes" value={fmtNum(cfopInconsistentes.length)} sub="Natureza vs Operação" icon={CheckCircle2} color={cfopInconsistentes.length > 0 ? "text-amber-500" : "text-emerald-500"} />
      </div>

      {/* Controle de Tabs */}
      <div className="flex gap-2 rounded-2xl p-2 glass-card max-w-2xl mx-auto">
        <button className={tabStyle(tab === "ncm")} onClick={() => { setTab("ncm"); setQ(""); }}>
          <Hash className="size-5" /> Visão por NCM
        </button>
        <button className={tabStyle(tab === "cfop")} onClick={() => { setTab("cfop"); setQ(""); }}>
          <FileCode className="size-5" /> Visão por CFOP
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input 
          value={q} 
          onChange={(e) => setQ(e.target.value)}
          placeholder={tab === "ncm" ? "Buscar por código NCM ou nome de produto…" : "Buscar por código CFOP ou descrição…"}
          className="w-full pl-10 pr-3 py-3 rounded-xl text-sm bg-white/5 border border-border outline-none focus:ring-2 focus:ring-primary/50 transition-all dark:text-white glass" 
        />
        {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-primary font-bold">Limpar</button>}
      </div>

      {/* Tabela NCM */}
      {tab === "ncm" && (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-slate-50/5 dark:bg-slate-900/50">
                <tr className="text-muted-foreground">
                  <th className="text-left p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th" onClick={() => toggleSortNcm("ncm")}>
                    <div className="flex items-center gap-1">Código NCM <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                  </th>
                  <th className="text-left p-4 font-bold text-[10px] uppercase">Amostragem de Produtos</th>
                  <th className="text-right p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th" onClick={() => toggleSortNcm("count")}>
                    <div className="flex items-center justify-end gap-1">Ocorrências <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                  </th>
                  <th className="text-right p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th" onClick={() => toggleSortNcm("vTotal")}>
                    <div className="flex items-center justify-end gap-1">Receita / Custo <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                  </th>
                  <th className="text-left p-4 font-bold text-[10px] uppercase">Status de Validação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {ncmList.map((n) => {
                  const invalido = !n.ncm || n.ncm === "SEM NCM" || n.ncm.length < 8;
                  const pct = totalGeral > 0 ? (n.vTotal / totalGeral) * 100 : 0;
                  return (
                    <tr key={n.ncm} className="hover:bg-primary/5 transition-colors group">
                      <td className="p-4">
                        <span className={`font-mono text-xs px-2 py-1 rounded-md font-bold border ${invalido ? "bg-red-500/10 text-red-500 border-red-500/30 dark:bg-red-500/20 dark:text-red-400" : "bg-sky-500/10 text-sky-600 border-sky-500/30 dark:bg-sky-500/20 dark:text-sky-300"}`}>{n.ncm}</span>
                      </td>
                      <td className="p-4 text-xs font-medium text-slate-700 dark:text-slate-300 max-w-[350px] truncate" title={[...n.produtos].join(", ")}>
                        {[...n.produtos].slice(0, 3).join(", ")}{n.produtos.size > 3 && <span className="text-[10px] opacity-50 ml-1">(+{n.produtos.size - 3})</span>}
                      </td>
                      <td className="p-4 text-right tabular-nums text-muted-foreground">{fmtNum(n.count)}</td>
                      <td className="p-4 text-right">
                        <div className="flex flex-col items-end">
                          <span className="font-bold tabular-nums dark:text-white">{fmtBRL(n.vTotal)}</span>
                          <span className="text-[10px] text-muted-foreground">{pct.toFixed(1)}% do Vol.</span>
                        </div>
                      </td>
                      <td className="p-4">
                        {invalido
                          ? <span className="text-[10px] font-bold flex items-center gap-1.5 text-red-500"><AlertCircle className="size-3.5" /> INCONSISTENTE</span>
                          : <span className="text-[10px] font-bold flex items-center gap-1.5 text-emerald-500"><CheckCircle2 className="size-3.5" /> VÁLIDO</span>}
                      </td>
                    </tr>
                  );
                })}
                {ncmList.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">Nenhum NCM encontrado na busca.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tabela CFOP */}
      {tab === "cfop" && (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-slate-50/5 dark:bg-slate-900/50">
                <tr className="text-muted-foreground">
                  <th className="text-left p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th" onClick={() => toggleSortCfop("cfop")}>
                    <div className="flex items-center gap-1">Código CFOP <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                  </th>
                  <th className="text-left p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th" onClick={() => toggleSortCfop("tipo")}>
                    <div className="flex items-center gap-1">Natureza <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                  </th>
                  <th className="text-left p-4 font-bold text-[10px] uppercase">Descrição Base</th>
                  <th className="text-right p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th" onClick={() => toggleSortCfop("notas")}>
                    <div className="flex items-center justify-end gap-1">Qtd NFs <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                  </th>
                  <th className="text-right p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th" onClick={() => toggleSortCfop("count")}>
                    <div className="flex items-center justify-end gap-1">Itens <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                  </th>
                  <th className="text-right p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th" onClick={() => toggleSortCfop("vTotal")}>
                    <div className="flex items-center justify-end gap-1">Receita / Custo <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {cfopList.map((c) => {
                  const pct = totalGeral > 0 ? (c.vTotal / totalGeral) * 100 : 0;
                  return (
                    <tr key={c.cfop} className="hover:bg-primary/5 transition-colors group">
                      <td className="p-4">
                        <span className={`font-mono text-xs px-2 py-1 rounded-md font-bold border ${c.tipo === "Entrada" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:bg-emerald-500/20 dark:text-emerald-400" : c.tipo === "Saída" ? "bg-blue-500/10 text-blue-600 border-blue-500/30 dark:bg-blue-500/20 dark:text-blue-400" : "bg-white/10 text-muted-foreground"}`}>
                          {c.cfop}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${c.tipo === "Entrada" ? "text-emerald-500 bg-emerald-500/10" : c.tipo === "Saída" ? "text-blue-500 bg-blue-500/10" : "text-muted-foreground bg-slate-500/10"}`}>
                          {c.tipo}
                        </span>
                      </td>
                      <td className="p-4 text-xs font-medium text-slate-700 dark:text-slate-300 max-w-[250px] truncate" title={c.descricao}>
                        {c.descricao}
                      </td>
                      <td className="p-4 text-right tabular-nums text-muted-foreground">{fmtNum(c.notas)}</td>
                      <td className="p-4 text-right tabular-nums text-muted-foreground">{fmtNum(c.count)}</td>
                      <td className="p-4 text-right">
                        <div className="flex flex-col items-end">
                          <span className="font-bold tabular-nums dark:text-white">{fmtBRL(c.vTotal)}</span>
                          <span className="text-[10px] text-muted-foreground">{pct.toFixed(1)}% do Vol.</span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {cfopList.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">Nenhum CFOP encontrado na busca.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color }: { label: string; value: string; sub: string; icon: any; color: string }) {
  return (
    <div className="glass-card p-5 hover-lift group">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg group-hover:bg-primary group-hover:text-white transition-colors">
          <Icon className="size-4" />
        </div>
        <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">{label}</span>
      </div>
      <div className={`text-2xl font-black tabular-nums dark:text-white ${color}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}
